import { lazy, Suspense, useEffect, useState } from 'react';
import { TeamPageLoadingSkeleton } from './components/TeamPageLoadingSkeleton';
import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { useCurrentMatchdayWindow } from '@/hooks/useCurrentMatchday';
import { periodAxisFromFormat } from '@/lib/periodLabels';
import { useFetchTeam } from '@/hooks/useFetchTeam';
import { useAuthStore } from '@/stores/AuthStore';

const CreateTeamContainer = lazy(() =>
  import('./components/CreateTeamContainer').then(module => ({
    default: module.CreateTeamContainer,
  }))
);
const preloadCreateTeamContainer = () => import('./components/CreateTeamContainer');

export const TeamPage = () => {
  const sessionState = useAuthStore(s => s.sessionState);
  const userId = useAuthStore(s => s.userId);
  const {
    id: TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    format: tournamentFormat,
    hasResolved: hasResolvedTournamentCalendar,
  } = useTeamCreateTournamentCalendarId();
  const isAuthenticated = sessionState === 'authenticated';

  const currentMatchdayQuery = useCurrentMatchdayWindow(TEAM_CREATE_TOURNAMENT_CALENDAR_ID, {
    enabled: hasResolvedTournamentCalendar && !!TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    source: isAuthenticated ? 'team' : 'public',
    userId: userId ?? undefined,
    format:
      tournamentFormat === 'classic_season_long' || tournamentFormat === 'knockout'
        ? tournamentFormat
        : undefined,
  });
  const existingTeamQuery = useFetchTeam(userId ?? undefined, TEAM_CREATE_TOURNAMENT_CALENDAR_ID, {
    enabled: hasResolvedTournamentCalendar && isAuthenticated && !!userId,
  });

  const [isCreateChunkReady, setIsCreateChunkReady] = useState(false);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const preload = async () => {
      await preloadCreateTeamContainer();
      if (isMounted) {
        setIsCreateChunkReady(true);
      }
    };

    void preload();
    return () => {
      isMounted = false;
    };
  }, []);

  const canBootstrap =
    hasResolvedTournamentCalendar &&
    sessionState !== 'resolving' &&
    (!isAuthenticated || !userId || !existingTeamQuery.isPending) &&
    !currentMatchdayQuery.isPending &&
    isCreateChunkReady;

  useEffect(() => {
    if (canBootstrap) {
      setHasBootstrapped(true);
    }
  }, [canBootstrap]);

  const hasSettledTeamLookup =
    !isAuthenticated || !userId || (!existingTeamQuery.isPending && !existingTeamQuery.isFetching);

  const isGameweekFormat = periodAxisFromFormat(tournamentFormat) === 'gameweek';

  if (!hasBootstrapped) {
    return <TeamPageLoadingSkeleton />;
  }

  return (
    <Suspense fallback={<TeamPageLoadingSkeleton />}>
      <CreateTeamContainer
        existingTeam={hasSettledTeamLookup ? (existingTeamQuery.data ?? null) : null}
        isAuthReady={sessionState !== 'resolving'}
        hasSettledTeamLookup={hasSettledTeamLookup}
        currentMatchdayMeta={currentMatchdayQuery.data?.currentMatchday}
        nextMatchdayMeta={currentMatchdayQuery.data?.nextMatchday}
        currentGameweekMeta={currentMatchdayQuery.data?.currentGameweek}
        nextGameweekMeta={currentMatchdayQuery.data?.nextGameweek}
        isGameweekFormat={isGameweekFormat}
      />
    </Suspense>
  );
};
